
import React, { useState, useMemo, useEffect } from 'react';
import { FastForward, Eraser, WrapText, ChevronDown, ChevronRight, BookOpen, Pencil, AlertCircle, RefreshCw } from 'lucide-react';
import { parseTerminologyRules, findMatchingRules, findMatchingSynonymGroupsForSegments, GlossaryRule } from '../../../utils/glossaryUtils';
import { SynonymGroup } from '../../../types';

interface Step1InputProps {

    sourceText: string;
    setSourceText: (text: string) => void;
    rawSegments: any[];
    hoveredSegmentIndex: number | null;
    setHoveredSegmentIndex: (index: number | null) => void;
    autoSegmentSource: () => void;
    onContinue: () => void;
    onStartTranslation: () => void;
    isTranslating: boolean;
    terminologyRules?: string;
    initialExcludedRules?: string[];
    onExcludedRulesChange?: (excluded: string[]) => void;
    onEditRules?: () => void;
    synonymGroups?: SynonymGroup[];
    onPreviewTranslationPrompt?: () => void;
}


const Step1Input: React.FC<Step1InputProps> = ({
    sourceText,
    setSourceText = (_text: string) => { },
    rawSegments = [],
    hoveredSegmentIndex = null,
    setHoveredSegmentIndex = (_index: number | null) => { },
    autoSegmentSource = () => { },
    onContinue = () => { },
    onStartTranslation = () => { },
    isTranslating = false,
    terminologyRules = '',
    initialExcludedRules = [],
    onExcludedRulesChange,
    onEditRules,
    synonymGroups = [],
    onPreviewTranslationPrompt
}) => {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const overlayRef = React.useRef<HTMLDivElement>(null);
    const [showMatchedRules, setShowMatchedRules] = useState(true);
    const [excludedRules, setExcludedRules] = useState<Set<string>>(new Set(initialExcludedRules));
    const [refreshKey, setRefreshKey] = useState(0); // For manual rematch


    // Sync excludedRules with parent when changed
    useEffect(() => {
        onExcludedRulesChange?.(Array.from(excludedRules));
    }, [excludedRules, onExcludedRulesChange]);


    // Match synonym groups only (legacy removed)
    const matchedGroups = useMemo(() =>
        findMatchingSynonymGroupsForSegments(sourceText, synonymGroups),
        [sourceText, synonymGroups, refreshKey]
    );

    // Flatten matched groups into simple rules format (source -> target)
    const allMatchedRules = useMemo(() => {
        return matchedGroups.map(match => {
            const defaultTarget = match.group.targets.find(t => t.isDefault)?.text || match.group.targets[0]?.text || '';
            return {
                source: match.match,
                sourceLower: match.match.toLowerCase(),
                target: defaultTarget,
                groupName: match.group.name,
                category: match.group.category
            };
        });
    }, [matchedGroups]);

    // Active rules = matched minus excluded
    const activeRules = useMemo(() =>
        allMatchedRules.filter(r => !excludedRules.has(r.sourceLower)),
        [allMatchedRules, excludedRules]
    );

    // Toggle rule exclusion
    const toggleRule = (sourceLower: string) => {
        setExcludedRules(prev => {
            const next = new Set(prev);
            if (next.has(sourceLower)) {
                next.delete(sourceLower);
            } else {
                next.add(sourceLower);
            }
            return next;
        });
    };

    const handleScroll = (e: React.UIEvent<HTMLTextAreaElement>) => {
        if (overlayRef.current) {
            overlayRef.current.scrollTop = e.currentTarget.scrollTop;
        }
    };

    return (
        <div className="flex flex-col h-full w-full max-w-[95%] mx-auto overflow-hidden pb-4">
            {/* Header with Action Buttons */}
            <div className="mb-4 flex items-center justify-between shrink-0">
                <div>
                    <h2 className="text-2xl font-black text-slate-800 tracking-tight">Input Source Document</h2>
                    <p className="text-slate-500 font-medium text-sm">Paste Vietnamese content on the left, see auto-segmented preview on the right.</p>
                </div>
                <div className="flex gap-3">
                    {onPreviewTranslationPrompt && (
                        <button
                            onClick={onPreviewTranslationPrompt}
                            className="px-4 py-3 bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-700 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center gap-2"
                            title="Preview the AI prompt before translating"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                            Preview Prompt
                        </button>
                    )}
                    <button onClick={onContinue} className="px-6 py-3 bg-slate-100 hover:bg-slate-200 border border-slate-200 text-slate-700 rounded-xl font-bold text-xs uppercase tracking-widest transition-all">Review Benchmark</button>
                    <button onClick={onStartTranslation} disabled={!sourceText.trim() || isTranslating} className="px-8 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl font-bold text-xs uppercase tracking-widest shadow-lg transition-all flex items-center gap-2">
                        <FastForward className="w-4 h-4" /> Start Translating
                    </button>
                </div>
            </div>

            {/* Three Panel Layout - Always show 3 columns */}
            <div className="flex-1 grid gap-4 min-h-0 grid-cols-[1fr_1fr_280px]">
                {/* Left Panel - Raw Input */}
                <div className="relative group flex flex-col min-h-0">
                    <div className="absolute -inset-1 bg-gradient-to-r from-indigo-200 to-blue-200 rounded-2xl opacity-40 blur group-hover:opacity-60 transition duration-500"></div>
                    <div className="relative flex-1 bg-white rounded-2xl border border-slate-200 shadow-lg flex flex-col overflow-hidden min-h-0">
                        <div className="px-6 py-3 border-b border-slate-100 flex justify-between items-center bg-slate-50 shrink-0">
                            <span className="text-xs font-bold text-indigo-600 uppercase tracking-widest flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                Raw Input
                            </span>
                            <button onClick={() => setSourceText('')} className="text-xs text-slate-400 hover:text-red-500 font-bold uppercase tracking-wider flex items-center gap-2 transition-colors">
                                <Eraser className="w-3 h-3" /> Clear
                            </button>
                        </div>
                        <div className="flex-1 min-h-0 relative">
                            {/* Highlighter Overlay */}
                            <div
                                ref={overlayRef}
                                className="absolute inset-0 p-6 pointer-events-none text-base leading-relaxed font-sans overflow-hidden whitespace-pre-wrap text-transparent z-0"
                                style={{ scrollbarGutter: 'stable' }}
                            >
                                {(rawSegments || []).map((seg, idx) => {
                                    const before = idx === 0 ? sourceText.substring(0, seg.start) : sourceText.substring((rawSegments[idx - 1]?.end || 0), seg.start);
                                    return (
                                        <React.Fragment key={idx}>
                                            {before}
                                            <span className={`transition-all duration-150 rounded px-0.5 -mx-0.5 ${hoveredSegmentIndex === idx ? 'bg-indigo-500/10 ring-2 ring-indigo-500/30' : ''}`}>
                                                {seg.text}
                                            </span>
                                        </React.Fragment>
                                    );
                                })}
                                {(rawSegments || []).length > 0 && sourceText.substring((rawSegments[rawSegments.length - 1]?.end || 0))}
                            </div>
                            {/* Real Textarea */}
                            <textarea
                                ref={textareaRef}
                                value={sourceText}
                                onChange={(e) => setSourceText(e.target.value)}
                                onScroll={handleScroll}
                                placeholder="Paste your Vietnamese text here..."
                                className="w-full h-full min-h-full bg-transparent text-base text-slate-700 p-6 outline-none resize-none leading-relaxed font-sans relative z-10 custom-scrollbar"
                                spellCheck={false}
                                style={{ scrollbarGutter: 'stable' }}
                            />
                        </div>
                        <div className="px-6 py-2 border-t border-slate-100 bg-slate-50 text-xs text-slate-400">
                            {sourceText.length} characters
                            <span className="mx-2 text-slate-300">|</span>
                            <span className={sourceText.length > 200000 ? "text-amber-500 font-bold" : ""}>
                                ~{Math.ceil(sourceText.length / 4)} tokens
                            </span>
                            {sourceText.length > 200000 && (
                                <span className="ml-2 text-amber-600 flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" />
                                    Approaching Output Limit (65k)
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Panel - Segmented Preview + Matched Rules */}
                <div className="relative group flex flex-col min-h-0">
                    <div className="absolute -inset-1 bg-gradient-to-r from-emerald-200 to-teal-200 rounded-2xl opacity-40 blur group-hover:opacity-60 transition duration-500"></div>
                    <div className="relative flex-1 bg-white rounded-2xl border border-slate-200 shadow-lg flex flex-col overflow-hidden min-h-0">
                        <div className="px-6 py-3 border-b border-slate-100 flex justify-between items-center bg-slate-50 shrink-0">
                            <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest flex items-center gap-2">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 10-4.243 4.243 3 3 0 004.243-4.243zm0-5.758a3 3 0 10-4.243-4.243 3 3 0 004.243 4.243z" /></svg>
                                Segmented Preview ({rawSegments.length} segments)
                            </span>
                            <button onClick={autoSegmentSource} className="text-xs text-indigo-600 hover:text-indigo-700 font-bold uppercase tracking-wider flex items-center gap-2 px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 transition-colors">
                                <WrapText className="w-3 h-3" /> Auto-Clean Format
                            </button>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-4 space-y-4">
                            {rawSegments.length === 0 ? (
                                <div className="text-center py-12 text-slate-400">
                                    <WrapText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">Enter text on the left to see segmented preview.</p>
                                </div>
                            ) : (
                                rawSegments.map((seg, idx) => {
                                    // Highlight matched terms in segment text
                                    const highlightTerms = (text: string) => {
                                        if (activeRules.length === 0) return text;
                                        const DASH_CHARS = '\u002D\u2010\u2011\u2012\u2013\u2014\u2015\u2212\uFE58\uFE63\uFF0D';
                                        const dashClass = `[${DASH_CHARS}]`;
                                        const normDash = (s: string) => s.replace(new RegExp(`[${DASH_CHARS}]`, 'g'), '-');

                                        let result: (string | React.ReactNode)[] = [text];
                                        activeRules.forEach((rule, ruleIdx) => {
                                            const newResult: (string | React.ReactNode)[] = [];
                                            result.forEach((part, partIdx) => {
                                                if (typeof part !== 'string') {
                                                    newResult.push(part);
                                                    return;
                                                }
                                                const escaped = rule.source.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                                                const dashAware = escaped.replace(/\\-|-/g, dashClass);
                                                const regex = new RegExp(`(${dashAware})`, 'gi');
                                                const parts = part.split(regex);
                                                parts.forEach((p, i) => {
                                                    if (normDash(p.toLowerCase()) === normDash(rule.source.toLowerCase())) {
                                                        newResult.push(
                                                            <span key={`${ruleIdx}-${partIdx}-${i}`} className="bg-amber-100 text-amber-800 px-0.5 rounded font-semibold" title={`\u2192 ${rule.target}`}>
                                                                {p}
                                                            </span>
                                                        );
                                                    } else if (p) {
                                                        newResult.push(p);
                                                    }
                                                });
                                            });
                                            result = newResult;
                                        });
                                        return result;
                                    };

                                    return (
                                        <div
                                            key={idx}
                                            onMouseEnter={() => setHoveredSegmentIndex(idx)}
                                            onMouseLeave={() => setHoveredSegmentIndex(null)}
                                            className={`flex gap-3 p-4 rounded-xl transition-all duration-300 border ${hoveredSegmentIndex === idx
                                                ? 'bg-indigo-50 border-indigo-200 shadow-md transform -translate-x-1'
                                                : 'bg-slate-50 border-slate-100'
                                                }`}
                                        >
                                            <div className="flex flex-col items-center gap-1">
                                                <span className={`text-[10px] font-black w-6 h-6 rounded-full flex items-center justify-center border transition-colors ${hoveredSegmentIndex === idx ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-400 border-slate-200'
                                                    }`}>
                                                    {idx + 1}
                                                </span>
                                            </div>
                                            <p className="flex-1 text-sm text-slate-700 leading-relaxed font-medium">
                                                {highlightTerms(seg.text)}
                                            </p>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Panel - Matched Rules (Always Visible) */}
                <div className="relative group flex flex-col min-h-0">
                    <div className="absolute -inset-1 bg-gradient-to-r from-amber-200 to-orange-200 rounded-2xl opacity-40 blur group-hover:opacity-60 transition duration-500"></div>
                    <div className="relative flex-1 bg-white rounded-2xl border border-slate-200 shadow-lg flex flex-col overflow-hidden min-h-0">
                        <div className="px-4 py-3 border-b border-slate-100 flex justify-between items-center bg-slate-50 shrink-0">
                            <span className="text-xs font-bold text-amber-600 uppercase tracking-widest flex items-center gap-2">
                                <BookOpen className="w-4 h-4" />
                                Matched Rules
                            </span>
                            <div className="flex items-center gap-2">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${activeRules.length > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-500'}`}>
                                    {activeRules.length}/{allMatchedRules.length}
                                </span>
                                {onEditRules && (
                                    <button
                                        onClick={onEditRules}
                                        className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                        title="Edit Terminology Rules"
                                    >
                                        <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                )}
                                <button
                                    onClick={() => setRefreshKey(k => k + 1)}
                                    className="p-1 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                                    title="Rematch Rules"
                                >
                                    <RefreshCw className="w-3.5 h-3.5" />
                                </button>
                            </div>

                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-3">
                            {allMatchedRules.length === 0 ? (
                                <div className="text-center py-8 text-slate-400">
                                    <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-xs">No terms matched</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {allMatchedRules.map((rule, idx) => {
                                        const isActive = !excludedRules.has(rule.sourceLower);
                                        const hasGroupName = 'groupName' in rule && rule.groupName;
                                        return (
                                            <label
                                                key={idx}
                                                className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-all ${isActive
                                                    ? 'bg-white border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50'
                                                    : 'bg-slate-50 border-slate-200 opacity-50'
                                                    }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isActive}
                                                    onChange={() => toggleRule(rule.sourceLower)}
                                                    className="w-4 h-4 mt-0.5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className={`text-xs font-semibold ${isActive ? 'text-amber-700' : 'text-slate-400 line-through'}`}>
                                                        {rule.source}
                                                        {hasGroupName && (
                                                            <span className="ml-2 text-[9px] font-normal text-indigo-500 bg-indigo-50 px-1.5 py-0.5 rounded border border-indigo-100">
                                                                {(rule as any).groupName}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className={`text-xs ${isActive ? 'text-emerald-600' : 'text-slate-400 line-through'}`}>
                                                        → {rule.target}
                                                    </div>
                                                </div>
                                            </label>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div className="px-4 py-2 border-t border-slate-100 bg-slate-50 text-[10px] text-slate-400 shrink-0">
                            Un-tick to exclude from prompt
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Step1Input;
